import React, { useState, useEffect } from 'react';
import {
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  Grid
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { shades } from '../../../theme';

const Education = ({ onValidityChange, onDataChange, initialData = {} }) => {
  // Initialize formData as an array with one default entry
  const [formData, setFormData] = useState([{
    degree: '',
    institutionName: '',
    graduationYear: '',
    inProgress: false,
    achievements: ''
  }]);

  const [errors, setErrors] = useState([{}]);

  // Load saved data from localStorage when component mounts
  useEffect(() => {
    const storedData = localStorage.getItem('educationData');
    if (storedData) {
      const data = JSON.parse(storedData);
      setFormData(data);
      setErrors(Array(data.length).fill({}));
    }
  }, []);

  // Save data to localStorage whenever any field changes
  useEffect(() => {
    localStorage.setItem('educationData', JSON.stringify(formData));

    // Send data to parent component if onDataChange is provided
    if (onDataChange) {
      onDataChange(formData);
    }
  }, [formData, onDataChange]);

  // Validate form
  const validateForm = () => {
    let isValid = true;
    const newErrors = formData.map(entry => {
      const entryErrors = {};

      if (!entry.degree) {
        entryErrors.degree = 'Degree is required.';
        isValid = false;
      }

      if (!entry.institutionName) {
        entryErrors.institutionName = 'Institution Name is required.';
        isValid = false;
      }

      if (!entry.inProgress && !entry.graduationYear) {
        entryErrors.graduationYear = 'Graduation Year is required if not still in progress.';
        isValid = false;
      }

      if (entry.graduationYear && (isNaN(entry.graduationYear) ||
        entry.graduationYear < 1900 ||
        entry.graduationYear > 2100)) {
        entryErrors.graduationYear = 'Please enter a valid year between 1900 and 2100.';
        isValid = false;
      }

      return entryErrors;
    });

    setErrors(newErrors);
    return isValid;
  };

  // Automatically check form validity whenever any field changes
  useEffect(() => {
    const isValid = formData.every(entry =>
      entry.degree &&
      entry.institutionName &&
      (entry.inProgress || (entry.graduationYear &&
        !isNaN(entry.graduationYear) &&
        entry.graduationYear >= 1900 &&
        entry.graduationYear <= 2100))
    );
    if (onValidityChange) {
      onValidityChange(isValid);
    }
    if (isValid && onDataChange) {
      onDataChange(formData);
    }
    validateForm();
  }, [formData, onValidityChange, onDataChange]);

  // Style configuration for text fields
  const textFieldStyle = {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#9e9e9e'
      },
      '&:hover fieldset': {
        borderColor: '#1976d2'
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff !important',
        '& fieldset': {
          borderColor: '#1976d2'
        }
      }
    },
    '& .MuiInputLabel-root': {
      color: '#9e9e9e'
    },
    '& .MuiOutlinedInput-input': {
      color: shades.neutral[700]
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
      transition: 'background-color 5000s ease-in-out 0s'
    }
  };

  const addEducationEntry = () => {
    setFormData([
      ...formData,
      {
        degree: '',
        institutionName: '',
        graduationYear: '',
        inProgress: false,
        achievements: ''
      }
    ]);
    setErrors([...errors, {}]);
  };

  const removeEducationEntry = (index) => {
    if (formData.length <= 1) {
      alert('You must have at least one education entry.');
      return;
    }

    const newEntries = [...formData];
    newEntries.splice(index, 1);
    setFormData(newEntries);

    const newErrors = [...errors];
    newErrors.splice(index, 1);
    setErrors(newErrors);
  };

  const handleChange = (index, field, value) => {
    const newEntries = [...formData];
    newEntries[index][field] = value;

    // If in progress is checked, clear graduation year
    if (field === 'inProgress' && value === true) {
      newEntries[index].graduationYear = '';
    }

    setFormData(newEntries);

    // Clear error for this field if it has a value now
    if (value) {
      const newErrors = [...errors];
      if (newErrors[index] && newErrors[index][field]) {
        newErrors[index] = { ...newErrors[index], [field]: '' };
        setErrors(newErrors);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, color: 'black', height: 'auto' }}>
      <Typography variant='h6' sx={{ color: shades.neutral[700], marginBottom: 1 }}>
        Education
      </Typography>

      {formData.map((entry, index) => (
        <Box
          key={index}
          sx={{
            border: `1px solid ${shades.neutral[300]}`,
            padding: 3,
            borderRadius: 1,
            position: 'relative',
            mb: 3
          }}
        >
          {/* Remove button - only show if there's more than one entry */}
          {formData.length > 1 && (
            <IconButton
              onClick={() => removeEducationEntry(index)}
              sx={{ position: 'absolute', top: 10, right: 10 }}
              color='error'
              size='small'
            >
              <DeleteIcon />
            </IconButton>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label='Degree *'
                fullWidth
                value={entry.degree}
                onChange={(e) => handleChange(index, 'degree', e.target.value)}
                error={!!errors[index]?.degree}
                helperText={errors[index]?.degree}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label='Institution Name *'
                fullWidth
                value={entry.institutionName}
                onChange={(e) => handleChange(index, 'institutionName', e.target.value)}
                error={!!errors[index]?.institutionName}
                helperText={errors[index]?.institutionName}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label='Graduation Year'
                type='number'
                fullWidth
                value={entry.graduationYear}
                onChange={(e) => handleChange(index, 'graduationYear', e.target.value)}
                disabled={entry.inProgress}
                error={!!errors[index]?.graduationYear}
                helperText={errors[index]?.graduationYear}
                InputProps={{ inputProps: { min: 1900, max: 2100 } }}
                sx={textFieldStyle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={entry.inProgress}
                    onChange={(e) => handleChange(index, 'inProgress', e.target.checked)}
                  />
                }
                label='Still in progress'
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label='Achievements/Notes (optional)'
                multiline
                rows={4}
                fullWidth
                value={entry.achievements}
                onChange={(e) => handleChange(index, 'achievements', e.target.value)}
                sx={textFieldStyle}
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      <Button
        variant='outlined'
        startIcon={<AddIcon />}
        onClick={addEducationEntry}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add Another Education
      </Button>
    </Box>
  );
};

export default Education;
